/**
 * Dock inferior de procesamiento en background — barridos de diagnóstico por servicio.
 *
 * No bloqueante y colapsable: se ancla al pie de la ventana, el usuario sigue
 * navegando y ve el avance real (paquete N de M) del servicio en curso más la
 * fila de los que esperan turno. La cola es SECUENCIAL: solo uno corre a la vez.
 *
 * El progreso se lee de la BD por polling (no de memoria del navegador): un
 * barrido lanzado en otra pestaña, por otro usuario o antes de recargar la
 * página se sigue viendo igual.
 *
 * El cuadro rojo de cada fila cancela: mata el barrido en curso o lo saca de la
 * fila. En ningún caso persiste resultados parciales — la última foto buena del
 * servicio queda intacta.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { cancelJob, listJobs, type ServiceDiagnosisJob } from "../api/serviceDiagnosis";
import { useAuth } from "../auth/useAuth";
import { useToast } from "./Toast";

const ROLES_DOCK = ["ADMIN", "ARQUITECTO"];
const POLL_ACTIVO_MS = 2_000;
const POLL_REPOSO_MS = 30_000;

function esActivo(j: ServiceDiagnosisJob): boolean {
  return j.status === "queued" || j.status === "running";
}

function colorEstado(status: ServiceDiagnosisJob["status"]): string {
  switch (status) {
    case "running":
      return "#3b82f6";
    case "queued":
      return "#94a3b8";
    case "done":
      return "#16a34a";
    case "canceled":
      return "#d97706";
    default:
      return "#dc2626";
  }
}

function pct(j: ServiceDiagnosisJob): number {
  const total = j.total_packages || 0;
  return total > 0 ? Math.round((j.done_packages / total) * 100) : 0;
}

/**
 * Línea de avance pedida por Carlos: "Procesando i/N → X% · paquete: Evaluando
 * REGLA". El paquete es la unidad de trabajo; el patrón es lo que ocurre dentro.
 */
function etiquetaEstado(j: ServiceDiagnosisJob): string {
  if (j.status === "queued") return "En cola";
  if (j.status === "running") {
    const total = j.total_packages || 0;
    if (!total) return "Preparando…";
    const cabeza = `Procesando ${j.done_packages + 1}/${total} → ${pct(j)}%`;
    const paquete = j.current_package ? ` · ${j.current_package.split("/").pop()}` : "";
    const regla = j.current_rule ? `: Evaluando ${j.current_rule}` : "";
    return `${cabeza}${paquete}${regla}`;
  }
  if (j.status === "done") return `Completo — ${j.done_packages} paquete(s)`;
  if (j.status === "canceled") return "Cancelado — sin resultados persistidos";
  return j.error_detail ?? "Error";
}

function Barra({ j }: { j: ServiceDiagnosisJob }) {
  const total = j.total_packages || 0;
  const avance = pct(j);
  // Sin total todavía (enumerando paquetes) la barra es indeterminada: se pinta
  // completa y atenuada en vez de mentir un 0 %.
  const indeterminada = j.status === "running" && total === 0;
  return (
    <div
      style={{
        height: 6, borderRadius: 3, background: "var(--sar-surface-2, #e2e8f0)",
        overflow: "hidden",
      }}
      role="progressbar"
      aria-valuenow={indeterminada ? undefined : avance}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Avance de ${j.service_name}`}
    >
      <div
        style={{
          height: "100%",
          width: indeterminada ? "100%" : `${avance}%`,
          background: colorEstado(j.status),
          opacity: indeterminada ? 0.35 : 1,
          transition: "width .3s ease",
        }}
      />
    </div>
  );
}

export function BackgroundDock() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const qc = useQueryClient();
  const [abierto, setAbierto] = useState(true);

  const puedeVer = user?.roles.some((r) => ROLES_DOCK.includes(r)) ?? false;

  const jobsQuery = useQuery({
    queryKey: ["service-diagnosis", "jobs"],
    queryFn: listJobs,
    enabled: puedeVer,
    refetchInterval: (query) => {
      const datos = query.state.data as ServiceDiagnosisJob[] | undefined;
      return datos?.some(esActivo) ? POLL_ACTIVO_MS : POLL_REPOSO_MS;
    },
    refetchIntervalInBackground: false,
  });

  const cancelar = useMutation({
    mutationFn: cancelJob,
    onSuccess: (j) => {
      showToast({
        kind: "info", title: `Barrido de ${j.service_name}`,
        message: "Cancelación solicitada — no se persistirá ningún resultado de esta corrida.",
      });
      void qc.invalidateQueries({ queryKey: ["service-diagnosis", "jobs"] });
    },
    onError: (err: unknown) => {
      const anyErr = err as { response?: { data?: { detail?: string } } };
      showToast({
        kind: "error", title: "No se pudo cancelar",
        message: anyErr?.response?.data?.detail ?? "Error desconocido",
      });
    },
  });

  const jobs = jobsQuery.data ?? [];
  const activos = jobs.filter(esActivo);

  // Cuando un barrido DEJA de estar activo, su corrida ya quedó escrita: se
  // invalidan las consultas del diagnóstico de servicio para que la pantalla
  // abierta muestre el resultado sin que el usuario tenga que recargar.
  const activosPrevios = useRef<Set<string>>(new Set());
  useEffect(() => {
    const ahora = new Set(activos.map((j) => j.id));
    const termino = [...activosPrevios.current].some((id) => !ahora.has(id));
    activosPrevios.current = ahora;
    if (termino) void qc.invalidateQueries({ queryKey: ["svc-diag"] });
  }, [activos, qc]);

  // En reposo el dock desaparece: no es una barra permanente, es el panel del
  // trabajo en curso. Los resultados finales se ven en la campana del topbar.
  if (!puedeVer || activos.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40,
        background: "var(--sar-surface, #ffffff)",
        borderTop: "1px solid var(--sar-border, #e2e8f0)",
        boxShadow: "0 -4px 16px rgba(15,23,42,.08)",
        maxHeight: "45vh", display: "flex", flexDirection: "column",
      }}
      aria-label="Procesos en background"
    >
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          padding: "6px 12px", border: 0, background: "transparent",
          cursor: "pointer", color: "var(--sar-text, #0f172a)", textAlign: "left",
        }}
        aria-expanded={abierto}
      >
        <span style={{ fontSize: 12, fontWeight: 700 }}>
          Procesos en background
        </span>
        <span
          style={{
            fontSize: 11, padding: "1px 7px", borderRadius: 999,
            background: "#3b82f6", color: "#fff", fontWeight: 700,
          }}
        >
          {activos.length}
        </span>
        <span style={{ fontSize: 11, color: "var(--sar-muted, #94a3b8)" }}>
          cola secuencial — un servicio a la vez
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11 }}>{abierto ? "▼" : "▲"}</span>
      </button>

      {abierto && (
        <div style={{ overflowY: "auto", padding: "0 12px 10px" }}>
          {activos.map((j) => (
            <div
              key={j.id}
              style={{
                display: "grid", gridTemplateColumns: "1fr auto",
                gap: 10, alignItems: "center", padding: "7px 0",
                borderTop: "1px solid var(--sar-border, #e2e8f0)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{j.service_name}</span>
                  {j.kind === "rule_sweep" && (
                    // La cola es compartida: sin esto el dock haría pasar por
                    // barrido completo lo que solo mide unas pocas reglas.
                    <span title={j.rule_ids.join(", ")}
                          style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px",
                                   borderRadius: 999, background: "#e0e7ff",
                                   color: "#3730a3" }}>
                      {j.rule_ids.length} regla(s)
                    </span>
                  )}
                  {j.branch && (
                    <span style={{ fontSize: 11, color: "var(--sar-muted, #94a3b8)" }}>
                      @{j.branch}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: colorEstado(j.status) }}>
                    {etiquetaEstado(j)}
                  </span>
                </div>
                <div style={{ marginTop: 5 }}>
                  <Barra j={j} />
                </div>
              </div>
              <button
                type="button"
                title={j.status === "queued" ? "Sacar de la fila" : "Cancelar barrido"}
                aria-label={j.status === "queued" ? "Sacar de la fila" : "Cancelar barrido"}
                disabled={j.cancel_requested || cancelar.isPending}
                onClick={() => cancelar.mutate(j.id)}
                style={{
                  width: 18, height: 18, borderRadius: 3, cursor: "pointer",
                  border: "1px solid #b91c1c",
                  background: j.cancel_requested ? "#fca5a5" : "#dc2626",
                  opacity: j.cancel_requested ? 0.6 : 1,
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
